/**
 * SecurityComplianceDashboard - Aggregated compliance metrics, trends, audit logs, and violations
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  ShieldCheck,
  AlertTriangle,
  Eye,
  Lock,
  Server,
  Clock,
  RefreshCw,
  Loader2,
  ChevronRight,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import {
  complianceAPI,
  ComplianceDashboardMetrics,
  BreakGlassTrend,
  AuditLogEntry,
  ViolationEntry,
  PolicySummary,
} from '@/services/api/compliance';
import { useFormattedDate } from '../../hooks/useUserTimezone';

interface SecurityComplianceDashboardProps {
  organizationId: number;
  organizationName: string;
  onNavigate?: (tab: string) => void;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function SecurityComplianceDashboard({
  organizationId,
  organizationName,
  onNavigate,
}: SecurityComplianceDashboardProps) {
  const { fmt } = useFormattedDate();
  const [metrics, setMetrics] = useState<ComplianceDashboardMetrics | null>(null);
  const [trends, setTrends] = useState<BreakGlassTrend[]>([]);
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [violations, setViolations] = useState<ViolationEntry[]>([]);
  const [policySummary, setPolicySummary] = useState<PolicySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, t, a, v, p] = await Promise.all([
        complianceAPI.getDashboardMetrics(organizationId),
        complianceAPI.getBreakGlassTrends(organizationId, 6),
        complianceAPI.getAuditLog(organizationId, { page: 1, page_size: 20 }),
        complianceAPI.getViolations(organizationId),
        complianceAPI.getPolicySummary(organizationId),
      ]);
      setMetrics(m);
      setTrends(t);
      setAuditEntries(a.entries);
      setViolations(v);
      setPolicySummary(p);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load compliance dashboard');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const trendData = trends.map((t) => ({
    name: `${MONTH_NAMES[t.month - 1]} ${t.year}`,
    Approved: t.approved,
    Pending: t.pending,
    Flagged: t.flagged,
    Violation: t.violation,
  }));

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500 mr-3" />
        <span className="text-gray-500 text-lg">Loading compliance dashboard...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
            <p className="text-[13px] text-ink-500">
              Overview for {organizationName}
            </p>
        </div>
        <button
          onClick={fetchAll}
          className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 flex items-center gap-1.5"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Metric Cards */}
      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
            label="Break Glass Events"
            sublabel="Last 30 days"
            value={metrics.total_break_glass_events}
            badge={
              metrics.pending_reviews > 0
                ? { text: `${metrics.pending_reviews} pending`, color: 'amber' }
                : undefined
            }
          />
          <MetricCard
            icon={<Eye className="w-5 h-5 text-blue-500" />}
            label="PII Accesses"
            sublabel="Last 30 days"
            value={metrics.pii_access_count}
          />
          <MetricCard
            icon={<XCircle className="w-5 h-5 text-red-500" />}
            label="Violations"
            sublabel="Flags & violations"
            value={metrics.violations_count}
            valueColor={metrics.violations_count > 0 ? 'text-red-600 dark:text-red-400' : undefined}
          />
          <MetricCard
            icon={<ShieldCheck className="w-5 h-5 text-green-500" />}
            label="Policy Coverage"
            sublabel="Programs shielded"
            value={
              policySummary
                ? `${policySummary.programs_shield_enabled}/${policySummary.total_programs}`
                : '-'
            }
          />
        </div>
      )}

      {/* Status Row */}
      {metrics && (
        <div className="flex items-center gap-4 flex-wrap">
          <StatusPill
            label="Data Shield"
            active={metrics.active_shields_count > 0}
            detail={`${metrics.active_shields_count} active`}
          />
          <StatusPill
            label="Encryption"
            active={metrics.encryption_status === 'active'}
            detail={metrics.encryption_status}
          />
          <StatusPill
            label="External Systems"
            active={metrics.external_systems_active > 0}
            detail={`${metrics.external_systems_active} connected`}
          />
          {policySummary && (
            <>
              <StatusPill
                label="Break Glass"
                active={policySummary.org_break_glass_enabled}
                detail={policySummary.org_break_glass_enabled ? 'Enabled' : 'Disabled'}
              />
              <StatusPill
                label="Audit Logging"
                active={policySummary.programs_audit_enabled > 0}
                detail={`${policySummary.programs_audit_enabled} programs`}
              />
            </>
          )}
        </div>
      )}

      {/* Break Glass Trends */}
      {trendData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Break Glass Trends</CardTitle>
            <CardDescription>Monthly events by review status (last 6 months)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Approved" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Pending" stackId="a" fill="#f59e0b" />
                <Bar dataKey="Flagged" stackId="a" fill="#f97316" />
                <Bar dataKey="Violation" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Violations Panel */}
      {violations.length > 0 && (
        <Card className="border-red-200 dark:border-red-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base text-red-700 dark:text-red-400">
                  Violations & Overdue Reviews
                </CardTitle>
                <CardDescription>
                  {violations.length} item{violations.length !== 1 ? 's' : ''} requiring attention
                </CardDescription>
              </div>
              {onNavigate && (
                <button
                  onClick={() => onNavigate('break-glass-management')}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                  Open Break Glass Audit <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-red-50 dark:bg-red-900/10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Date
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Initiator
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Reason
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Age
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {violations.map((v) => (
                    <tr key={v.id} className="hover:bg-red-50/50 dark:hover:bg-red-900/5">
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                        {v.created_at ? fmt.short(v.created_at) : '-'}
                      </td>
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                        {v.initiator_name || `User #${v.initiated_by}`}
                      </td>
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[200px]">
                        {v.reason}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            v.review_status === 'violation'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : v.review_status === 'flagged'
                              ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                              : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                          }`}
                        >
                          {v.review_status}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500">
                        {v.age_hours < 24
                          ? `${Math.round(v.age_hours)}h`
                          : `${Math.round(v.age_hours / 24)}d`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Audit Log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent PII Access Log</CardTitle>
          <CardDescription>Last 20 field-level access events</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {auditEntries.length === 0 ? (
            <div className="py-8 text-center">
              <Eye className="w-6 h-6 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No PII access events recorded</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Time
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      User
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Field
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Record
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Shield
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {auditEntries.map((entry) => (
                    <tr
                      key={entry.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 text-gray-700 dark:text-gray-300"
                    >
                      <td className="px-4 py-2 text-xs text-gray-400">
                        {entry.accessed_at
                          ? fmt.dateTime(entry.accessed_at)
                          : '-'}
                      </td>
                      <td className="px-4 py-2">
                        {entry.accessed_by_name || (entry.accessed_by ? `#${entry.accessed_by}` : '-')}
                      </td>
                      <td className="px-4 py-2">
                        <span className="font-mono text-xs">{entry.field_accessed || '-'}</span>
                        {entry.sensitivity_tier && (
                          <span className="ml-1 text-xs text-purple-600 dark:text-purple-400">
                            [{entry.sensitivity_tier}]
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs">{entry.access_type || '-'}</td>
                      <td className="px-4 py-2 text-xs">
                        {entry.table_name}#{entry.record_id}
                      </td>
                      <td className="px-4 py-2">
                        {entry.data_shield_active ? (
                          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Links */}
      {onNavigate && (
        <div className="grid grid-cols-3 gap-4">
          <QuickLink
            icon={<Lock className="w-5 h-5 text-blue-500" />}
            label="Data Privacy Settings"
            description="Manage org & program privacy policies"
            onClick={() => onNavigate('data-privacy-settings')}
          />
          <QuickLink
            icon={<AlertTriangle className="w-5 h-5 text-amber-500" />}
            label="Break Glass Audit"
            description="Review emergency access events"
            onClick={() => onNavigate('break-glass-management')}
          />
          <QuickLink
            icon={<Server className="w-5 h-5 text-purple-500" />}
            label="External Systems"
            description="Manage API integrations & keys"
            onClick={() => onNavigate('external-systems')}
          />
        </div>
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function MetricCard({
  icon,
  label,
  sublabel,
  value,
  badge,
  valueColor,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  value: number | string;
  badge?: { text: string; color: string };
  valueColor?: string;
}) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">{icon}</div>
            <p className={`text-2xl font-bold ${valueColor || 'text-gray-900 dark:text-gray-100'}`}>
              {value}
            </p>
            <p className="text-sm text-gray-700 dark:text-gray-300">{label}</p>
            <p className="text-xs text-gray-400">{sublabel}</p>
          </div>
          {badge && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                badge.color === 'amber'
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  : badge.color === 'red'
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {badge.text}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusPill({
  label,
  active,
  detail,
}: {
  label: string;
  active: boolean;
  detail: string;
}) {
  return (
    <div
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
        active
          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-green-500' : 'bg-gray-400'}`}
      />
      {label}: {detail}
    </div>
  );
}

function QuickLink({
  icon,
  label,
  description,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="text-left p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
    >
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
    </button>
  );
}
